<script setup lang="ts">
// NPM
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMq } from 'vue3-mq';

// Components
import { BaseCard, BaseEmptyState } from '@magma-app/magma-lapilli';
import BaseChallengeDetails from '@/components/bases/BaseChallengeDetails.vue';

// Stores
import { useCommonStore } from '@/stores/common';
import { useChallengesStore } from '@/stores/challenges';
import type { UserDashboardGetAllChallengesResponseType } from '@magma-app/magma-types/all';
const commonStore = useCommonStore();
const challengesStore = useChallengesStore();

// Plugins
const { t } = useI18n({ useScope: 'global' });
const mq = useMq();

// Reactive variables
const completedChallengeDetails = ref<
  UserDashboardGetAllChallengesResponseType['validated'][number] | null
>(null);

// Event Listeners
const onCloseCompletedChallengeDetails = () => {
  challengesStore.setCompletedChallengeDetails(false);
  completedChallengeDetails.value = null;
};

const onCompletedChallengeClick = (
  challenge: UserDashboardGetAllChallengesResponseType['validated'][number]
) => {
  if (!mq.lgPlus) {
    challengesStore.setCompletedChallengeDetails(true);
    completedChallengeDetails.value = challenge;
    window.scrollTo(0, 0);
  } else if (mq.lgPlus) {
    commonStore.setManageChallengesModal({
      description: challenge.description,
      open: true,
      item: challenge,
      role: 'challenge-status',
      title: challenge.name,
    });
  }
};

// Methods
const badgeStatus = (
  status: 'validated' | 'rejected' | 'toValidate' | 'interested' | null
) => {
  if (status === 'toValidate') {
    return {
      color: 'yellow',
      text: t('generic.validation.pending'),
    };
  } else if (status === 'rejected') {
    return {
      color: 'red',
      text: t('generic.validation.declined'),
    };
  } else {
    return {
      color: 'green',
      text: t('generic.validation.completed'),
    };
  }
};
</script>

<template>
  <div
    class="block px-4 md:px-8"
    :class="{
      'content-start lg:flex lg:flex-wrap':
        challengesStore.completedChallenges?.length,
      ' px-4 md:px-6': !completedChallengeDetails
    }"
  >
    <template v-if="challengesStore.completedChallenges?.length">
      <template v-if="!completedChallengeDetails">
        <BaseCard
          v-for="(
            challenge, challengeIndex
          ) in challengesStore.completedChallenges"
          :key="challengeIndex"
          class="mb-2 lg:mb-6 lg:mr-6"
          :title="challenge.name"
          :img-url="
            challenge.pictureUrl
              ? challenge.pictureUrl
              : mq.lgPlus
                ? '/img/challenge_lg.png'
                : '/img/challenge.png'
          "
          :points="challenge.points"
          :display-points="!challenge.pictureUrl"
          :badge-status="badgeStatus(challenge.status)"
          @click="onCompletedChallengeClick(challenge)"
        />
      </template>

      <BaseChallengeDetails
        v-else
        :challenge="completedChallengeDetails"
        :completed="!!completedChallengeDetails"
        @close="onCloseCompletedChallengeDetails"
      />
    </template>

    <BaseEmptyState
      v-else
      class="flex h-full flex-col items-center justify-center"
      :text="$t('completedChallengesView.empty.completed')"
      type="empty-list"
    />
  </div>
</template>
